import Vue from 'vue'
import Vuex from 'vuex'
import ApiRequest from '@/helpers/ApiRequest.js'
import i18n from '../i18n';

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    config: [],
    // translations - currently storing only tooltips. Other translations are local
    translations: {},
    configLoaded: false,
    sessionReloaded: 0,
    maintenanceOn: false
  },
  actions: {
    loadConfig ({ commit, dispatch }) {
      const config = localStorage.getItem('config');
      if (config) {
        commit('SAVE_CONFIG', JSON.parse(config));
        dispatch('reloadConfig');
      } else {
        commit('CLEAR_CONFIG');
        dispatch('reloadConfig');
      }
    },
    reloadConfig ({ commit, state }, payload = {}) {
      let path = 'config/get';
      if (payload && !payload.force && state.configLoaded && state.config.lastupdated) {
        path += '?lastupdated=' + state.config.lastupdated;
      }
      ApiRequest.request(path, ApiRequest.REQUEST_GET, [], function (response) {
        if (response && response.data && response.data !== true) {
          commit('SAVE_CONFIG', response.data);
        }
      });
    },
    loadTranslations ({ commit }) {
      // translations - currently loading only tooltips. Other translations are local
      const path = 'translations/tooltips';
      ApiRequest.request(path, ApiRequest.REQUEST_GET, [], function (response) {
        if (response && response.data) {
          commit('SAVE_TRANSLATIONS', response.data);
        }
      });
    },
    setMaintenanceOn ({ commit }) {
      commit('MAINTENANCE_ON');
    },
    sessionUpdated ({ commit }) {
      commit('SESSION_UPDATE');
    },
    checkSession ({ commit }) {
      if (ApiRequest.session.get('accessToken')) {
        ApiRequest.request('user/session', ApiRequest.REQUEST_GET, {}, function (response) {
          if (response && response.data && response.data.user) {
            const selectedClient = ApiRequest.session.get('selectedClientApiId');
            let clientPerm = ApiRequest.session.get('client_permissions');
            let neededUpdate = false;
            if (selectedClient && clientPerm[selectedClient] && clientPerm[selectedClient].needUpdate) {
              neededUpdate = true;
            }
            ApiRequest.session.set('user', response.data.user.name);
            ApiRequest.session.set('email', response.data.user.email);
            ApiRequest.session.set('permissions', response.data.user.permissions);
            ApiRequest.session.set('client_permissions', response.data.user.client_permissions);
            if (response.data.token) {
              ApiRequest.session.set('accessToken', response.data.token);
            }
            clientPerm = ApiRequest.session.get('client_permissions');
            if (!neededUpdate && selectedClient && clientPerm[selectedClient] && clientPerm[selectedClient].needUpdate) {
              ApiRequest.router.go(ApiRequest.router.currentRoute);
            }
            commit('SESSION_UPDATE', response.data.version);
          }
        });
      } else {
        commit('SESSION_UPDATE')
      }
    }
  },
  getters: {
    getD365SupportUrl: (state) => () => {
      const url = (process.env.VUE_APP_D365_URL ? process.env.VUE_APP_D365_URL : null);
      if (state.config && state.config.d365supporturl) {
        return state.config.d365supporturl;
      }
      return url;
    },
    getDataFromConfigWithApiId: (state) => (configKey, apiId) => {
      if (state.config[configKey]) {
        for (const id in state.config[configKey]) {
          if (state.config[configKey][id].id == apiId) {
            return state.config[configKey][id];
          }
        }
        return null;
      }
      return null;
    },
    getItemsFromConfigWithFilter: (state) => (configKey, filterKey = null, filterValue = null) => {
      if (state.config[configKey]) {
        if (filterKey === null) {
          return state.config[configKey]
        }
        const items = {};
        for (const id in state.config[configKey]) {
          if (state.config[configKey][id][filterKey] == filterValue) {
            items[id] = state.config[configKey][id];
          }
        }
        return items;
      }
      return null;
    },
    getDataFromConfigWithFilter: (state) => (configKey, filterKey, filterValue) => {
      if (state.config[configKey]) {
        if (filterKey === null) {
          return state.config[configKey]
        }
        for (const id in state.config[configKey]) {
          if (state.config[configKey][id][filterKey] == filterValue) {
            return state.config[configKey][id];
          }
        }
      }
      return null;
    },
    getParamFromConfig: (state) => (configKey, id, param) => {
      if (state.config[configKey]) {
        if (state.config[configKey][id]) {
          return state.config[configKey][id][param];
        }
        return state.config[configKey][Object.keys(state.config[configKey])[0]][param];
      }
      return null;
    },
    getParamFromConfigByFilter: (state, getters) => (configKey, filterKey, filterValue, param) => {
      const el = getters.getDataFromConfigWithFilter(configKey, filterKey, filterValue);
      if (el) {
        return el[param];
      }
      return null;
    },
    getApiIdFromConfig: (state, getters) => (configKey, id) => {
      return getters.getParamFromConfig(configKey, id, 'id');
    },
    getDataFromConfigById: (state) => (configKey, id) => {
      if (state.config[configKey] && state.config[configKey][id]) {
        return state.config[configKey][id];
      }
      return null;
    },
    getConfigOptionsWithIdValues: (state) => {
      return (configKey, locale = null, filterKey = null, filterValue = null, includeDeleted = false) => {
        const options = [];
        if (state.config[configKey]) {
          for (const id in state.config[configKey]) {
            if (!includeDeleted && state.config[configKey][id].deleted) {
              // skip deleted
            } else if (filterKey === null || state.config[configKey][id][filterKey] == filterValue) {
              options.push({
                value: parseInt(state.config[configKey][id].idx),
                text: (locale ? state.config[configKey][id].name[locale] : state.config[configKey][id].name),
                code: ((state.config[configKey][id].code) ? state.config[configKey][id].code : null)
              })
            }
          }
        }
        return options
      }
    },
    getConfigOptions: (state) => {
      return (configKey, locale = null, filterKey = null, filterValue = null, notFilter = false, includeDeleted = false, inValueFilter = false, extraParams = []) => {
        const options = [];
        if (state.config[configKey]) {
          for (const id in state.config[configKey]) {
            if (!includeDeleted && state.config[configKey][id].deleted) {
              // skip deleted
            } else {
              let valid = false;
              if (filterKey === null) {
                valid = true;
              } else if (inValueFilter) {
                if (notFilter && !filterValue.find(tmpVals => tmpVals == state.config[configKey][id][filterKey])) {
                  valid = true;
                } else if (!notFilter && filterValue && filterValue.find(tmpVals => tmpVals == state.config[configKey][id][filterKey])) {
                  valid = true;
                }
              } else if ((!notFilter && state.config[configKey][id][filterKey] == filterValue) || (notFilter && state.config[configKey][id][filterKey] != filterValue)) {
                valid = true;
              }
              if (valid) {
                let text = state.config[configKey][id].name;
                if (locale) {
                  if (text[locale]) {
                    text = text[locale];
                  } else {
                    text = text[i18n.fallbackLocale];
                  }
                }
                const optionEl = {
                  value: (state.config[configKey][id].id ? state.config[configKey][id].id : id),
                  api_id: state.config[configKey][id].api_id,
                  text: text,
                  code: ((state.config[configKey][id].code) ? state.config[configKey][id].code : null)
                };
                extraParams.forEach(function (param) {
                  optionEl[param] = state.config[configKey][id][param]
                });
                options.push(optionEl);
              }
            }
          }
        }
        return options
      }
    },
    getTranslationTooltip: (state) => (code, locale = null) => {
      let item = null;
      if (state.translations[code]) {
        item = state.translations[code].tooltip;
        if (locale) {
          if (item[locale]) {
            item = item[locale];
          } else {
            item = null;
          }
        }
      }
      return item;
    }
  },
  mutations: {
    CLEAR_CONFIG (state) {
      state.configLoaded = false;
    },
    SAVE_CONFIG (state, config) {
      let reloadPage = false;
      if (state.config && state.config.version && state.config.version !== config.version) {
        reloadPage = true;
      }
      state.config = config;
      state.configLoaded = true;
      state.maintenanceOn = false;
      if (reloadPage) {
        state.config = null;
        const params = ApiRequest.session.getAll();
        Object.keys(params).forEach(key => {
          if (key.indexOf('filters:') === 0) {
            ApiRequest.session.remove(key);
          }
        });
        localStorage.setItem('config', '');
        localStorage.setItem('translations', '');
        window.location.replace(window.location.href);
      } else {
        localStorage.setItem('config', JSON.stringify(config));
      }
    },
    SAVE_TRANSLATIONS (state, data) {
      state.translations = data;
    },
    SESSION_UPDATE (state, version) {
      state.sessionReloaded = (state.sessionReloaded + 1) % 10;
      if (version && state.config && state.config.version && state.config.version !== version) {
        state.config.version = version;
        const params = ApiRequest.session.getAll();
        Object.keys(params).forEach(key => {
          if (key.indexOf('filters:') === 0) {
            ApiRequest.session.remove(key);
          }
        });
        window.location.replace(window.location.href);
      }
    },
    MAINTENANCE_ON (state) {
      state.maintenanceOn = true;
    }
  }
})
